import React, { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, photoUrl } from "../lib/api";
import { toast } from "sonner";
import { ImagePlus, Trash2, Star, Loader2, X, ChevronLeft, ChevronRight } from "lucide-react";

const MAX_GALLERY = 12;

// Thumbnail with skeleton shimmer until the image loads.
function Thumb({ path, active, onClick }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`gallery-thumb-${path.split("/").pop()}`}
      className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border ${active ? "border-[#D4A017] ring-2 ring-[#D4A017]/40" : "border-[#D8E0E8]"}`}
    >
      {!loaded && <span className="absolute inset-0 animate-pulse bg-[#E3EAF0]" />}
      <img
        src={photoUrl(path)}
        alt=""
        loading="lazy"
        onLoad={() => setLoaded(true)}
        className={`h-full w-full object-cover transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}
      />
    </button>
  );
}

export function PhotoGallery({ member, canEdit, onChanged }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // index | null
  const [caption, setCaption] = useState("");
  const [year, setYear] = useState("");
  const gallery = member.gallery || [];
  const metaMap = Object.fromEntries((member.photo_meta || []).map((m) => [m.path, m]));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    const room = MAX_GALLERY - gallery.length;
    if (room <= 0) { toast.error(`Photo limit reached (${MAX_GALLERY}).`); return; }
    const toAdd = files.slice(0, room);
    if (files.length > room) toast.info(`Only ${room} more photo${room === 1 ? "" : "s"} can be added.`);
    setUploading(true);
    try {
      for (const f of toAdd) await api.uploadGalleryPhoto(member.id, f);
      toast.success(`Added ${toAdd.length} photo${toAdd.length === 1 ? "" : "s"}`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload photo");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (path) => {
    try { await api.deleteGalleryPhoto(member.id, path); toast.success("Photo removed"); onChanged?.(); setLightbox(null); }
    catch { toast.error("Could not remove photo"); }
  };
  const makeAvatar = async (path) => {
    try { await api.setGalleryAvatar(member.id, path); toast.success("Profile photo updated"); onChanged?.(); }
    catch { toast.error("Could not set profile photo"); }
  };

  const openPath = lightbox != null ? gallery[lightbox] : null;
  useEffect(() => {
    const meta = openPath ? metaMap[openPath] : null;
    setCaption(meta?.caption || "");
    setYear(meta?.year || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openPath]);

  const saveMeta = async () => {
    if (!openPath) return;
    try { await api.setPhotoMeta(member.id, openPath, caption, year); toast.success("Caption saved"); onChanged?.(); }
    catch { toast.error("Could not save caption"); }
  };

  if (!canEdit && gallery.length === 0) return null;

  const step = (d) => setLightbox((i) => (i == null ? i : (i + d + gallery.length) % gallery.length));

  return (
    <div className="mt-6" data-testid="photo-gallery">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wider text-[#64748B]">
          Photos {gallery.length > 0 && <span className="ml-1 text-[#94A3B8]">{gallery.length}/{MAX_GALLERY}</span>}
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || gallery.length >= MAX_GALLERY}
            data-testid="gallery-add-button"
            className="flex items-center gap-1 rounded-full border border-[#D8E0E8] bg-white px-2.5 py-1 text-xs font-medium text-[#082D4A] transition-colors hover:bg-[#E8EEF3] disabled:opacity-50"
          >
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} Add photo
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="gallery-file-input" />
      </div>

      {gallery.length === 0 ? (
        canEdit && <p className="text-xs text-[#94A3B8]">Add favourite photos of {member.full_name?.split(" ")[0] || "them"} — up to {MAX_GALLERY}.</p>
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {gallery.map((path, i) => (
            <div key={path} className="relative shrink-0">
              <Thumb path={path} active={member.photo_path === path} onClick={() => setLightbox(i)} />
              {metaMap[path]?.year && <span className="pointer-events-none absolute bottom-0 left-0 right-0 rounded-b-lg bg-black/55 py-0.5 text-center text-[9px] font-medium text-white">{metaMap[path].year}</span>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={lightbox != null} onOpenChange={(o) => { if (!o) setLightbox(null); }}>
        <DialogContent className="max-w-2xl border-none bg-[#141310] p-0" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">Photo of {member.full_name}</DialogTitle>
          <DialogDescription className="sr-only">A photo from {member.full_name}'s gallery with optional caption and year.</DialogDescription>
          {openPath && (
            <div className="relative">
              <img src={photoUrl(openPath)} alt="" className="max-h-[70vh] w-full rounded-t-lg object-contain bg-black" />
              {gallery.length > 1 && (
                <>
                  <button onClick={() => step(-1)} data-testid="gallery-prev" className="absolute left-2 top-1/3 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"><ChevronLeft size={20} /></button>
                  <button onClick={() => step(1)} data-testid="gallery-next" className="absolute right-2 top-1/3 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"><ChevronRight size={20} /></button>
                </>
              )}
              {/* caption / year */}
              {canEdit ? (
                <div className="flex flex-wrap items-end gap-2 border-t border-white/10 p-3">
                  <div className="min-w-[160px] flex-1">
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-white/50">Caption</label>
                    <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="What's happening here?" data-testid="gallery-caption-input" className="w-full rounded-md border border-white/15 bg-white/10 px-2 py-1.5 text-sm text-white placeholder:text-white/40 focus:outline-none" />
                  </div>
                  <div className="w-24">
                    <label className="mb-1 block text-[10px] uppercase tracking-wider text-white/50">Year</label>
                    <input value={year} onChange={(e) => setYear(e.target.value)} placeholder="1975" data-testid="gallery-year-input" className="w-full rounded-md border border-white/15 bg-white/10 px-2 py-1.5 text-sm text-white placeholder:text-white/40 focus:outline-none" />
                  </div>
                  <button onClick={saveMeta} data-testid="gallery-save-caption" className="rounded-md bg-[#D4A017] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#B0850F]">Save</button>
                </div>
              ) : (metaMap[openPath]?.caption || metaMap[openPath]?.year) ? (
                <div className="border-t border-white/10 p-3 text-sm text-white/90" data-testid="gallery-caption-display">
                  {metaMap[openPath]?.year && <span className="mr-2 rounded bg-white/15 px-1.5 py-0.5 text-xs">{metaMap[openPath].year}</span>}
                  {metaMap[openPath]?.caption}
                </div>
              ) : null}
              {canEdit && (
                <div className="flex items-center justify-end gap-2 px-3 pb-3">
                  <button onClick={() => makeAvatar(openPath)} disabled={member.photo_path === openPath} data-testid="gallery-set-avatar" className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-sm text-white hover:bg-white/20 disabled:opacity-40">
                    <Star size={14} className={member.photo_path === openPath ? "fill-[#D4A017] text-[#D4A017]" : ""} /> {member.photo_path === openPath ? "Profile photo" : "Set as profile"}
                  </button>
                  <button onClick={() => removePhoto(openPath)} data-testid="gallery-delete" className="flex items-center gap-1.5 rounded-full bg-red-500/80 px-3 py-1.5 text-sm text-white hover:bg-red-600">
                    <Trash2 size={14} /> Remove
                  </button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
